/**
 * AuthContext - Discord OAuth Authentication
 * Context per gestione autenticazione via Discord OAuth
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { clearStoredAuth, getApiBaseUrl, getValidAuthToken } from '@/lib/api'

export interface User {
  id: string
  username: string
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MARRANI' | 'MODERATOR' | 'VIP' | 'MEMBER' | 'TRIAL' | 'GUEST'
  discord_id?: string
  avatar?: string
  email?: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: () => void
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  isAdmin: () => boolean
  isSuperAdmin: () => boolean
  hasRole: (role: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
  getToken: () => string | null
  getAuthHeaders: () => Promise<Record<string, string>>
}

const AuthContext = createContext<AuthContextType | null>(null)

const decodeJwtPayload = (jwt: string): Record<string, any> | null => {
  try {
    const [, payloadPart] = jwt.split('.')
    if (!payloadPart) return null

    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'))

  const checkAuth = useCallback(async () => {
    const storedToken = await getValidAuthToken()
    const storedUser = localStorage.getItem('auth_user')
    
    if (!storedToken) {
      setUser(null)
      setIsLoading(false)
      return
    }

    let restoredUser: User | null = null

    // Se abbiamo i dati utente salvati, usiamoli
    if (storedUser) {
      try {
        restoredUser = JSON.parse(storedUser)
      } catch {
        localStorage.removeItem('auth_user')
      }
    }

    if (!restoredUser) {
      const payload = decodeJwtPayload(storedToken)
      if (payload) {
        restoredUser = {
          id: String(payload.sub || payload.user_id || ''),
          username: payload.username || payload.email?.split('@')?.[0] || 'admin',
          role: (payload.role || 'ADMIN') as User['role'],
          discord_id: payload.discord_id
        }
        localStorage.setItem('auth_user', JSON.stringify(restoredUser))
      }
    }

    setUser(restoredUser)
    setToken(storedToken)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const login = useCallback(() => {
    // Redirect to Discord OAuth
    const apiBase = getApiBaseUrl()
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/discord/callback`)
    window.location.href = `${apiBase}/api/v1/auth/discord/login?redirect_uri=${redirectUri}`
  }, [])

  const logout = useCallback(async () => {
    try {
      const storedToken = await getValidAuthToken()
      if (storedToken) {
        const apiBase = getApiBaseUrl()
        await fetch(`${apiBase}/api/v1/auth/discord/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${storedToken}`
          }
        })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearStoredAuth()
      setUser(null)
      setToken(null)
    }
  }, [])

  const isAdmin = useCallback(() => {
    return user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN'
  }, [user])

  const isSuperAdmin = useCallback(() => {
    return user?.role === 'SUPER_ADMIN'
  }, [user])

  const hasRole = useCallback((role: string) => {
    return user?.role === role
  }, [user])

  const hasAnyRole = useCallback((roles: string[]) => {
    return user ? roles.includes(user.role) : false
  }, [user])

  const getToken = useCallback(() => {
    return token
  }, [token])

  const getAuthHeaders = useCallback(async () => {
    const currentToken = await getValidAuthToken()
    if (!currentToken) return {}
    return {
      'Authorization': `Bearer ${currentToken}`
    }
  }, [])

  useEffect(() => {
    const onAuthExpired = () => {
      setUser(null)
      setToken(null)
      setIsLoading(false)
    }
    const onAuthRefreshed = () => {
      setToken(localStorage.getItem('auth_token'))
    }
    window.addEventListener('ummr-auth-expired', onAuthExpired)
    window.addEventListener('ummr-auth-refreshed', onAuthRefreshed)
    return () => {
      window.removeEventListener('ummr-auth-expired', onAuthExpired)
      window.removeEventListener('ummr-auth-refreshed', onAuthRefreshed)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!token,
        login,
        logout,
        checkAuth,
        isAdmin,
        isSuperAdmin,
        hasRole,
        hasAnyRole,
        getToken,
        getAuthHeaders
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

// Export as useAuth for backward compatibility
export const useAuth = useAuthContext
